import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Loading } from 'react-admin';
import { useErasureDisposition } from './useErasureDisposition';
import { DISPOSITION_COLOR, DISPOSITION_LABEL } from './erasureState';

/**
 * The per-member DISPOSITION-PLAN preview (D-erasure) - the core SAFETY surface. It renders the exact
 * blast radius BEFORE execute: per record type, what erasure would do (tombstone / anonymise-retain /
 * delete / untouched), how many records the member has, and whether the mock owns the transformation
 * or it is the backend seam. The inverse of the DSAR package: the same inventory shown as a
 * disposition plan, not a data dump.
 */
export const ErasureDispositionPreview = ({
  subjectUserId,
}: {
  subjectUserId: number;
}) => {
  const { plan, isLoading } = useErasureDisposition(subjectUserId);

  if (isLoading || !plan) return <Loading />;

  return (
    <Box sx={{ mt: 2 }} data-testid={`erasure-disposition-${subjectUserId}`}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Disposition plan: what erasing {plan.memberName || 'this member'} would
        do
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
        Reviewed before execute. The audit trail is never erased; records under
        a tax, AML, financial or proof-of-consent obligation are anonymised and
        retained, the person severed.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Record type</TableCell>
            <TableCell>Disposition</TableCell>
            <TableCell align="right">Records</TableCell>
            <TableCell>Who acts</TableCell>
            <TableCell>Note</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {plan.lines.map((line) => (
            <TableRow
              key={line.key}
              data-testid={`erasure-disp-row-${line.key}`}
            >
              <TableCell>{line.label}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="filled"
                  color={DISPOSITION_COLOR[line.disposition]}
                  label={DISPOSITION_LABEL[line.disposition]}
                  data-testid={`erasure-disp-${line.key}-${line.disposition}`}
                />
              </TableCell>
              <TableCell align="right">
                {line.count === null ? 'n/a' : line.count}
              </TableCell>
              <TableCell>{line.mockOwned ? 'This panel' : 'Backend'}</TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {line.note}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
